import { ref, type Ref } from 'vue'
import { DEFAULT_COMIC_SORT, isComicSortOrder, type ComicSortOrder } from './comic-sort'
import { DEFAULT_COLLECTION_SORT, isCollectionSortOrder, type CollectionSortOrder } from './collection-order'

export { DEFAULT_COLLECTION_SORT }

/** 作品清單的呈現形式。書架式封面格線是預設。 */
export const libraryViewModes = ['shelf', 'list'] as const

export type LibraryViewMode = typeof libraryViewModes[number]

export const DEFAULT_VIEW_MODE: LibraryViewMode = 'shelf'

export function isLibraryViewMode(value: unknown): value is LibraryViewMode {
  return typeof value === 'string' && (libraryViewModes as readonly string[]).includes(value)
}

const SORT_KEY = 'tanko.library.sortOrder'
/**
 * 一次性修復的標記：把既有雲端目錄項的建立時間對回本地的加入時間。
 * 重跑是無害的（註冊冪等），因此用 localStorage 就夠，不值得為它開一張表。
 */
const ORDER_REPAIR_KEY = 'tanko.catalog.orderRepaired'
const VIEW_MODE_KEY = 'tanko.library.viewMode'
/** 集合頁自己的顯示順序；顯示方式（書架／列表）與書庫共用 VIEW_MODE_KEY。 */
const COLLECTION_SORT_KEY = 'tanko.collection.sortOrder'

/**
 * 偏好一律以短字串存放，不存 JSON——JSON 解析失敗是多出來的一類錯誤，
 * 而這裡要存的只有一個枚舉值。
 *
 * 讀取的簽章沒有失敗路徑：鍵不存在、值不在白名單、或 localStorage 本身無法存取
 * （無痕視窗封鎖網站資料、瀏覽器設定、非瀏覽器環境）一律回傳預設值，不記錄錯誤、
 * 不顯示訊息。偏好只是便利，讀不到不該讓書庫壞掉。
 */
function read<T>(key: string, guard: (value: unknown) => value is T, fallback: T): T {
  try {
    const stored = globalThis.localStorage?.getItem(key)
    return guard(stored) ? stored : fallback
  } catch { return fallback }
}

/** 寫入失敗（配額、隱私模式）吞掉：畫面上的選擇仍然生效於本次工作階段。 */
function write(key: string, value: string): void {
  try { globalThis.localStorage?.setItem(key, value) }
  catch { /* 偏好寫不進去不影響當次操作。 */ }
}

export function readSortOrder(): ComicSortOrder {
  return read(SORT_KEY, isComicSortOrder, DEFAULT_COMIC_SORT)
}

export function writeSortOrder(order: ComicSortOrder): void {
  write(SORT_KEY, order)
}

export function readCollectionSortOrder(): CollectionSortOrder {
  return read(COLLECTION_SORT_KEY, isCollectionSortOrder, DEFAULT_COLLECTION_SORT)
}

export function writeCollectionSortOrder(order: CollectionSortOrder): void {
  write(COLLECTION_SORT_KEY, order)
}

export function readViewMode(): LibraryViewMode {
  return read(VIEW_MODE_KEY, isLibraryViewMode, DEFAULT_VIEW_MODE)
}

export function writeViewMode(mode: LibraryViewMode): void {
  write(VIEW_MODE_KEY, mode)
}

/**
 * 共用的顯示偏好。本地與私人雲端兩個範圍讀同一份，因此不存在兩份需要互相同步的
 * 狀態——切換分頁時不可能顯示過期的選擇。
 *
 * 單例惰性初始化：第一次存取才讀 localStorage，讓上面那些純函式在單元測試中被
 * import 時不會碰到全域環境。
 */
type DisplayPreferences = {
  sortOrder: Ref<ComicSortOrder>
  viewMode: Ref<LibraryViewMode>
  collectionSortOrder: Ref<CollectionSortOrder>
  setSortOrder(order: ComicSortOrder): void
  setViewMode(mode: LibraryViewMode): void
  setCollectionSortOrder(order: CollectionSortOrder): void
}

let shared: DisplayPreferences | undefined

export function useLibraryDisplay(): DisplayPreferences {
  if (!shared) {
    const sortOrder = ref<ComicSortOrder>(readSortOrder())
    const viewMode = ref<LibraryViewMode>(readViewMode())
    const collectionSortOrder = ref<CollectionSortOrder>(readCollectionSortOrder())
    shared = {
      sortOrder,
      viewMode,
      collectionSortOrder,
      setSortOrder(order) { sortOrder.value = order; writeSortOrder(order) },
      setViewMode(mode) { viewMode.value = mode; writeViewMode(mode) },
      setCollectionSortOrder(order) { collectionSortOrder.value = order; writeCollectionSortOrder(order) },
    }
  }
  return shared
}

export function catalogOrderRepaired(): boolean {
  return read(ORDER_REPAIR_KEY, (value): value is 'done' => value === 'done', 'pending') === 'done'
}

export function markCatalogOrderRepaired(): void {
  write(ORDER_REPAIR_KEY, 'done')
}
